import { datadogLogs } from "@datadog/browser-logs";
import { ErrorTag } from "@redotech/cockroachdb-codegen/congodb/error-tag";
import { ReviewingParty } from "@redotech/cockroachdb-codegen/congodb/reviewing-party";
import { UpsellBillingTrigger } from "@redotech/cockroachdb-codegen/congodb/upsell-billing-trigger";
import { GetReturnErrorCode } from "@redotech/customer-portal-sdk/rpc/schema/get-return-enums";
import { ConversionFlagKey } from "@redotech/feature-flags-keys-conversion/keys";
import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { Order } from "@redotech/redo-order-core-common/order-schema";
import { returnToTrackable } from "@redotech/redo-order-tracking-trackable-aggregate/trackable";
import {
  ReturnAddressZod,
  ReturnWarning,
  ReturnZod,
} from "@redotech/redo-return-core-common/return";
import { PickupPayer } from "@redotech/redo-return-core-common/return-flow-enums";
import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import {
  getReturnTotalsFrontendLogFn,
  ReturnTotals,
  ReturnTotalsCalculator,
} from "@redotech/redo-return-core-common/return-totals-calculator";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { allProductsAreRepair } from "@redotech/redo-return-core-common/returns/all-products-are-repair";
import { hasPendingNanitRmaLabelMetadata } from "@redotech/redo-return-core-common/returns/nanit-rma";
import {
  getFieldsFromReturnTotalsSet,
  ReturnTotalsType,
} from "@redotech/redo-return-core-common/returns/return-totals/return-totals-set";
import { SerializedReturnSummaryTotals } from "@redotech/redo-return-core-common/returns/return-totals/types";
import { getResource } from "@redotech/redo-team-common/localization/resource";
import { ClientError } from "@redotech/rpc/errors";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { RpcClientContext } from "../../contexts/rpc";
import { ReturnAddressType } from "../../navigator";
import { CustomerPortalAppSettings } from "../../settings";
import {
  localizeCustomerPortalFlowText,
  ReturnAddressToAddress,
  ReturnItemParams,
  subtitleWithoutDefault,
} from "../../util";
import { AddressInfo } from "../useAddress/use-address";
import {
  useCreationStatus,
  UseCreationStatusResult,
} from "./use-creation-status";
import { usePickupModals } from "./use-pickup-modals";
import { useSteps, UseStepsResponse } from "./use-steps";
export enum TopStatus {
  SUCCESS = "success",
  WARNING = "warning",
  ERROR = "error",
}

export enum ReturnSubmissionResultStatus {
  CREATED_RETURN = "successfully_created_return",
  CREATED_RETURN_WITH_ERRORS = "created_return_with_errors",
  REJECTED_RETURN = "rejected_return",
}

export type ErrorCodeInfo =
  | {
      type: GetReturnErrorCode.ReturnNotFound;
      data: { texts: { title: string } };
    }
  | undefined;

export interface AlertProps {
  title: string;
  subtitle: string;
  primaryAction: { onClick: () => void; text: string } | undefined;
  secondaryAction: { onClick: () => void; text: string } | undefined;
}
interface ReturnConfirmationResponse extends UseStepsResponse {
  returnHeader: {
    title: string;
    subtitle: string;
    onClick: (() => void) | undefined;
    onClickTitle: string | undefined;
    status: TopStatus;
    alerts: AlertProps[] | undefined;
  };
  error: string | undefined;
  errorCodeData: ErrorCodeInfo | undefined;
  loadingReturn: boolean;
  rejected: boolean;
  rejectHtml: string;
  customSuccessMessage: string | undefined;
  warnings: ReturnWarning[] | undefined;
  deadlineDays: number;
  showDeadline: boolean | undefined;
  newItems: ReturnItemParams[] | undefined;
  products: ReturnItemParams[] | undefined;
  currency: Currency;
  newItemsHeader: string;
  showAddresses: boolean;
  newOrderAddress: AddressInfo | undefined;
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    id: string;
  };
  returnAddress: AddressInfo | undefined;
  returnTotals: ReturnTotals | undefined;
  summaryFeesHeader: string;
  summaryFeesOrderHeader: string;
  returnItemsHeader: string;
  draftOrderUrl: string | undefined;
  texts: { rejectedText: string; returnItems: string; newItems: string };
  labelDeducted: boolean;
  creationStatus: UseCreationStatusResult["creationStatus"];
  showCreationStatus: boolean;
  returnType: ReturnTypeEnum;

  shouldShowSummary: boolean;
  manualLabelInfo: {
    showManualLabel: boolean;
    instructions: string[] | undefined;
  };
  manualLabelData?: {
    shipments?:
      | {
          address: any;
          returnItemIds: string[];
          packingSlipUrl?: string | null;
        }[]
      | null;
  } | null;
  showReturnSteps: boolean;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
  presentmentCurrency: string;
  exchangeRate: number;
  returnData: ReturnZod | undefined;
  pickupModals: ReturnType<typeof usePickupModals>;
}

export function returnConfirmationQueryKey(
  returnId: string,
  completelyFinished: boolean,
) {
  return ["returnConfirmation", returnId, completelyFinished];
}

export const useReturnConfirmation = ({
  settings,
  returnId,
  navigateToCheckout,
  isMerchantCreated = false,
  useTemporalCreationStatus = true,
  hasCreationStatusError = false,
}: {
  settings: CustomerPortalAppSettings | undefined;
  returnId: string;
  navigateToCheckout: (orderUrl: string) => void;
  isMerchantCreated?: boolean;
  useTemporalCreationStatus?: boolean;
  hasCreationStatusError?: boolean;
}): ReturnConfirmationResponse => {
  const { t } = useTranslation();
  const client = useRequiredContext(RpcClientContext);
  const creationStatusResult = useCreationStatus({ returnId });
  const showCreationStatus =
    !creationStatusResult.completelyFinished || hasCreationStatusError;

  useEffect(() => {
    if (showCreationStatus || !isMerchantCreated) {
      return;
    }
    const targetOrigin =
      process.env.NODE_ENV === "production"
        ? "https://app.getredo.com"
        : "http://localhost:3003";
    window?.top?.postMessage({ returnId }, targetOrigin);
  }, [showCreationStatus, returnId, isMerchantCreated]);

  const returnQueryKey = returnConfirmationQueryKey(
    returnId,
    creationStatusResult.completelyFinished,
  );

  const returnQuery = useQuery({
    queryKey: returnQueryKey,
    queryFn: async () => {
      try {
        const {
          return: returnData,
          originOrders,
          loyaltyData,
        } = await client.getReturn({ returnId });
        const { orders } = (await client.getOrders({
          orderIds: returnData.orders.map((order) => order.order),
        })) as unknown as { orders: Order[] };

        return { returnData, orders, originOrders, loyaltyData };
      } catch (e: any) {
        if (e instanceof ClientError) {
          datadogLogs.logger.error(
            "Client error retrieving return after submission",
            {
              code: e.code,
              statusCode: e.statusCode,
              message: e.message,
              returnId,
            },
          );
        }
        datadogLogs.logger.error("Error retrieving return after submission", {
          e,
          returnId,
        });
        if (e?.code) {
          const returnErrorCode = e.code as GetReturnErrorCode;
          switch (returnErrorCode) {
            case GetReturnErrorCode.ReturnNotFound:
              throw e;
            default:
              assertNever(returnErrorCode);
          }
        }
        if (e?.message?.includes("Output validation")) {
          throw new Error(
            t(
              "Validation Error: Your return was submitted successfully, but there was an issue parsing your return, please contact support at support@getredo.com with the return id {{returnId}}",
              { returnId },
            ),
          );
        }

        throw new Error(
          t(
            "Your return was submitted successfully, but there was an issue loading your return. Please refresh the page to load your return.",
          ),
        );
      }
    },
    enabled:
      !!returnId &&
      (!useTemporalCreationStatus || creationStatusResult.completelyFinished),
    retry: false,
  });

  const { returnData, orders, originOrders, loyaltyData } =
    returnQuery.data ?? {};
  const rejected = returnData?.status === ReturnStatus.REJECTED;

  const presentmentCurrency = returnData?.presentmentCurrency ?? Currency.USD;
  const exchangeRate = returnData?.exchangeRate ?? 1;

  const baseCurrency = returnData?.currency ?? Currency.USD;
  const shouldConvertCurrency = !!(
    exchangeRate &&
    exchangeRate > 0 &&
    presentmentCurrency &&
    presentmentCurrency !== baseCurrency
  );

  const returnTotalsSetQuery = useQuery({
    queryKey: [
      "returnTotalsSet",
      returnData?._id,
      shouldConvertCurrency,
      exchangeRate,
    ],
    queryFn: async () => {
      if (
        !returnData ||
        !settings ||
        !orders ||
        !loyaltyData ||
        !originOrders
      ) {
        throw new Error("Required data for return totals is not available");
      }

      let convertedReturnData = returnData;
      if (shouldConvertCurrency && exchangeRate) {
        convertedReturnData = {
          ...returnData,
          products: returnData.products.map((product) => ({
            ...product,
            price: product.variant_id
              ? (+product.price * exchangeRate).toString()
              : product.price,
          })),
          advancedExchangeItems: returnData.advancedExchangeItems?.map(
            (item) => ({
              ...item,
              price: item.variantId
                ? (
                    +(item.price || 0) * (returnData.newItemToExchangeRate ?? 1)
                  ).toString()
                : item.price,
            }),
          ),
          new_order_taxes: (
            +(returnData.new_order_taxes || "0") *
            (returnData.newItemToExchangeRate ?? 1)
          ).toString(),
        };
      }

      const calculator = new ReturnTotalsCalculator({
        return_: convertedReturnData as any, //TODO type this
        order: orders[0] as any,
        orders: orders as any,
        team: { settings },
        originOrders: originOrders ?? [{ error: "No origin order" }],
        loyaltyData,
        redoCoverageProductIds: new Set(settings?.redoCoverageProductIds ?? []),
        logFn: getReturnTotalsFrontendLogFn(),
        newItemToExchangeRate: returnData.newItemToExchangeRate ?? 1,
        isInPresentmentCurrency: shouldConvertCurrency,
      });
      const legacyTotals = calculator.getTotalsForAllProducts();

      let returnSummaryTotals: SerializedReturnSummaryTotals | undefined =
        undefined;
      const { returnTotalsTypeToUse } = await client.getReturnTotalsFeatureFlag(
        {},
      );
      if (returnTotalsTypeToUse === ReturnTotalsType.NEW) {
        returnSummaryTotals = (
          await client.getReturnTotals({ returnId: returnData._id })
        ).returnSummaryTotals;
      }

      return {
        legacy: legacyTotals,
        new: returnSummaryTotals,
        typeToUse: returnTotalsTypeToUse,
      };
    },
    enabled:
      !!returnData && !!settings && !!orders && !!loyaltyData && !!originOrders,
  });

  const newItems = useMemo(() => {
    if (!returnData || !returnTotalsSetQuery.data) {
      return [];
    }

    const items: ReturnItemParams[] = [];

    // Advanced exchange items
    if (returnData.advancedExchangeItems) {
      items.push(
        ...returnData.advancedExchangeItems.map((item) => ({
          id: item.variantId,
          pendingReturnItemId: "",
          imageSrc: item.images?.[0] ?? "",
          title: item.title ?? "",
          subtitle: item.variantTitle ?? "",
          quantity: item.quantity ?? 1,
          variants: [],
          formattedPrice: item.price ?? "0",
          isSelected: true,
          currency: shouldConvertCurrency
            ? presentmentCurrency
            : (returnData.currency ?? Currency.USD),
          price:
            returnTotalsSetQuery.data?.typeToUse === ReturnTotalsType.NEW
              ? (item.priceSet?.presentmentMoney?.amount ?? item.price)
              : (item.price ?? "0"),
          itemValue:
            returnTotalsSetQuery.data?.typeToUse === ReturnTotalsType.NEW
              ? (item.itemValueSet?.presentmentMoney?.amount ?? item.itemValue)
              : (item.itemValue ?? "0"),
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate: returnData.newItemToExchangeRate ?? 1,
          collections: [],
        })),
      );
    }

    const returnTotals = returnTotalsSetQuery.data
      ? getFieldsFromReturnTotalsSet({
          returnTotalsSet: returnTotalsSetQuery.data,
        })
      : undefined;
    // Variant exchange items from products
    for (const product of returnData.products) {
      const productTotals = returnTotals?.productTotals.find(
        (pt) =>
          pt.product.variant_id === product.variant_id &&
          pt.product.line_item_id === product.line_item_id,
      );

      const price = productTotals?.originalPrice?.toString() ?? "0";
      const itemValue = productTotals?.discountPrice?.toString() ?? "0";

      if (product.exchange_for) {
        items.push({
          id: product.exchange_for.variant_id,
          pendingReturnItemId: "",
          imageSrc: product.exchange_for.images?.[0] ?? "",
          title: product.exchange_for.product_title,
          subtitle: product.exchange_for.variant_title,
          quantity: product.quantity ?? 1,
          variants: [],
          formattedPrice: price,
          isSelected: true,
          currency: returnData.currency ?? Currency.USD,
          price,
          itemValue,
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate: returnData.newItemToExchangeRate ?? 1,
          collections: [],
        });
      }

      if (product.exchangeGroupItem) {
        items.push({
          id: product.exchangeGroupItem.variantId,
          pendingReturnItemId: "",
          imageSrc: product.exchangeGroupItem.imageSrc ?? "",
          title: product.exchangeGroupItem.title,
          subtitle: product.exchangeGroupItem.variantTitle,
          quantity: product.quantity ?? 1,
          variants: [],
          formattedPrice: price,
          isSelected: true,
          currency: returnData.currency ?? Currency.USD,
          price,
          itemValue,
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate: returnData.newItemToExchangeRate ?? 1,
          collections: [],
        });
      }
    }

    return items;
  }, [
    returnData,
    returnTotalsSetQuery.data,
    presentmentCurrency,
    shouldConvertCurrency,
  ]);

  /**
   * Return warnings are displayed to the customer with a support code, but are not actionable
   */
  const warnings = useMemo(() => {
    // Filter our irrelevant warnings
    return returnData?.warnings?.filter((warning: ReturnWarning) => {
      if (warning.type === ErrorTag.Label && returnData.shipment) {
        return false;
        // All pickup warnings will be converted to alerts
      } else if (warning.type === ErrorTag.Pickup) {
        return false;
      } else if (
        warning.type === ErrorTag.InstantRefund &&
        returnData.instantRefund
      ) {
        return false;
        // Claim report warnings are internal and should not be shown to the customer
      } else if (warning.type === ErrorTag.ClaimReport) {
        return false;
      }
      return true;
    });
  }, [returnData]);

  const pickupModals = usePickupModals({ returnData });

  const hasUnresolvedPickupWarnings = useMemo(() => {
    return (
      returnData?.warnings?.some(
        (warning) => warning.type === ErrorTag.Pickup,
      ) &&
      !!returnData.pickup &&
      !returnData.shipment?.pickup
    );
  }, [returnData]);

  const hasCustomItems = useMemo(
    () => returnData?.products.some((product) => product.isCustomItem) ?? false,
    [returnData],
  );

  /**
   * Alerts are warnings that have specific actions that the customer can take to resolve the underlying issue
   */
  const alerts: AlertProps[] | undefined = useMemo(() => {
    const result: AlertProps[] = [];
    if (hasUnresolvedPickupWarnings) {
      const isMerchantCoveredPickup =
        returnData?.pickup?.pickupPayer === PickupPayer.MERCHANT;
      result.push({
        title: t("Package pickup failed"),
        subtitle: isMerchantCoveredPickup
          ? t(
              "Your return was successfully submitted, but we couldn't schedule your package pickup. Please try again or cancel the pickup.",
            )
          : t(
              "Your return was successfully submitted, but we couldn't schedule your package pickup. Please try again or cancel the pickup. If you don't reschedule, the pickup fee will be refunded once your item arrives at our warehouse.",
            ),
        primaryAction: {
          onClick: () => {
            pickupModals.setRescheduleModalOpen(true);
          },
          text: t("Try again"),
        },
        secondaryAction: {
          onClick: () => {
            pickupModals.setCancelModalOpen(true);
          },
          text: isMerchantCoveredPickup
            ? t("Cancel pickup")
            : t("Cancel and refund pickup"),
        },
      });
    }
    if (hasCustomItems) {
      result.push({
        title: t("Product values may be adjusted"),
        subtitle: t(
          "Because you couldn't find your product, the product values shown are based on what you provided. We'll confirm and adjust them when we review your submission.",
        ),
        primaryAction: undefined,
        secondaryAction: undefined,
      });
    }
    return result.length > 0 ? result : undefined;
  }, [
    hasUnresolvedPickupWarnings,
    hasCustomItems,
    returnData,
    pickupModals,
    t,
  ]);

  const returnType = returnData?.type
    ? (returnData.type as ReturnTypeEnum)
    : ReturnTypeEnum.RETURN;
  const isPackageProtectionPlus =
    returnType === ReturnTypeEnum.CLAIM &&
    returnData?.reviewingParty === ReviewingParty.Redo &&
    settings?.featureFlags?.[
      ConversionFlagKey.PACKAGE_PROTECTION_PLUS_ID_COLLECTION_ENABLED
    ] === true;
  const copyResource = getResource({
    variant: returnData?.type || "return",
    overrides: settings?.resourceOverride,
  });
  const defaultCopyResource = getResource({
    variant: returnData?.type || "return",
  });

  const returnSubmissionResultStatus = useMemo(() => {
    if (!warnings) {
      return undefined;
    } else if (rejected) {
      return ReturnSubmissionResultStatus.REJECTED_RETURN;
    } else if (warnings?.length) {
      return ReturnSubmissionResultStatus.CREATED_RETURN_WITH_ERRORS;
    } else {
      return ReturnSubmissionResultStatus.CREATED_RETURN;
    }
  }, [rejected, warnings]);

  const needToPayDraftOrder =
    returnData?.type === "return" &&
    returnData.draftOrderURL &&
    !returnData.exchangeOrder?.length &&
    returnData.upsellBillingTrigger !== UpsellBillingTrigger.ReturnCreated;

  const defaultDraftOrderHeaderText =
    returnType === ReturnTypeEnum.CLAIM
      ? t("Your claim submission is almost complete")
      : t("Your return submission is almost complete");
  const defaultDraftOrderSubtitleText =
    returnType === ReturnTypeEnum.CLAIM
      ? t(
          "Pay for your exchange to receive your return label and complete your claim submission.",
        )
      : t(
          "Pay for your exchange to receive your return label and complete your submission.",
        );
  const defaultSubmitHeaderText =
    returnType === ReturnTypeEnum.CLAIM
      ? t("Your claim has been submitted")
      : returnType === ReturnTypeEnum.WARRANTY
        ? t("Your warranty claim has been submitted")
        : returnType === ReturnTypeEnum.RECYCLE
          ? t("Your recycling request has been submitted")
          : returnType === ReturnTypeEnum.WITHDRAWAL
            ? t("Your withdrawal request has been submitted")
            : t("Your return has been submitted");
  const defaultSubmitWithErrorsHeaderText =
    returnType === ReturnTypeEnum.CLAIM
      ? t("There were problems submitting your claim")
      : returnType === ReturnTypeEnum.WARRANTY
        ? t("There were problems submitting your warranty claim")
        : returnType === ReturnTypeEnum.RECYCLE
          ? t("There were problems submitting your recycling request")
          : returnType === ReturnTypeEnum.WITHDRAWAL
            ? t("There were problems submitting your withdrawal request")
            : t("There were problems submitting your return");
  const defaultRejectHeaderText =
    returnType === ReturnTypeEnum.CLAIM
      ? t("Your claim has been rejected")
      : returnType === ReturnTypeEnum.WARRANTY
        ? t("Your warranty claim has been rejected")
        : returnType === ReturnTypeEnum.RECYCLE
          ? t("Your recycling request has been rejected")
          : returnType === ReturnTypeEnum.WITHDRAWAL
            ? t("Your withdrawal request has been rejected")
            : t("Your return has been rejected");
  const defaultConfirmationSubtext = isPackageProtectionPlus
    ? t(
        "Claims are reviewed by a trusted third-party provider and are typically processed within 1-3 business days. You'll receive an email as soon as your claim has been processed.",
      )
    : returnType === ReturnTypeEnum.CLAIM
      ? t(
          "We have received your submission. Your claim will be processed soon.",
        )
      : returnType === ReturnTypeEnum.WARRANTY
        ? t(
            "We have received your submission. Your warranty claim will be processed soon.",
          )
        : returnType === ReturnTypeEnum.RECYCLE
          ? t(
              "We have received your submission. Your recycling request will be processed soon.",
            )
          : returnType === ReturnTypeEnum.WITHDRAWAL
            ? t(
                "We have received your submission. Your withdrawal will be processed soon.",
              )
            : t(
                "We have received your submission. Your return will be processed soon.",
              );
  const pendingNanitRmaLabelSubtext = t(
    "We have received your submission. We'll email your return label once it's ready.",
  );
  const hasPendingNanitRmaLabel = hasPendingNanitRmaLabelMetadata(
    returnData?.metadata,
  );

  const returnHeader = useMemo(() => {
    if (needToPayDraftOrder) {
      return {
        title:
          copyResource.DRAFT_ORDER_HEADER ===
          defaultCopyResource.DRAFT_ORDER_HEADER
            ? defaultDraftOrderHeaderText
            : copyResource.DRAFT_ORDER_HEADER,
        subtitle:
          copyResource.DRAFT_ORDER_SUBTITLE ===
          defaultCopyResource.DRAFT_ORDER_SUBTITLE
            ? defaultDraftOrderSubtitleText
            : copyResource.DRAFT_ORDER_SUBTITLE,
        onClick: () => navigateToCheckout(returnData?.draftOrderURL ?? ""),
        onClickTitle: t("Pay for exchange order"),
        status: TopStatus.WARNING,
        alerts,
      };
    }
    switch (returnSubmissionResultStatus) {
      case undefined:
        return {
          title: "",
          subtitle: "",
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.SUCCESS,
          alerts: undefined,
        };
      case ReturnSubmissionResultStatus.CREATED_RETURN:
        return {
          title:
            copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT ===
            defaultCopyResource.CONFIRMATION_HEADER_TEXT_SUBMIT
              ? defaultSubmitHeaderText
              : copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT,
          subtitle: isPackageProtectionPlus
            ? defaultConfirmationSubtext
            : hasPendingNanitRmaLabel
              ? pendingNanitRmaLabelSubtext
              : copyResource.CONFIRMATION_HEADER_SUBTEXT ===
                  defaultCopyResource.CONFIRMATION_HEADER_SUBTEXT
                ? defaultConfirmationSubtext
                : copyResource.CONFIRMATION_HEADER_SUBTEXT,
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.SUCCESS,
          alerts,
        };
      case ReturnSubmissionResultStatus.CREATED_RETURN_WITH_ERRORS:
        return {
          title:
            copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT_WITH_ERRORS ===
            defaultCopyResource.CONFIRMATION_HEADER_TEXT_SUBMIT_WITH_ERRORS
              ? defaultSubmitWithErrorsHeaderText
              : copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT_WITH_ERRORS,
          subtitle:
            copyResource.CONFIRMATION_HEADER_SUBTEXT ===
            defaultCopyResource.CONFIRMATION_HEADER_SUBTEXT
              ? defaultConfirmationSubtext
              : copyResource.CONFIRMATION_HEADER_SUBTEXT,
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.ERROR,
          alerts,
        };
      case ReturnSubmissionResultStatus.REJECTED_RETURN:
        return {
          title:
            copyResource.CONFIRMATION_HEADER_TEXT_REJECT ===
            defaultCopyResource.CONFIRMATION_HEADER_TEXT_REJECT
              ? defaultRejectHeaderText
              : copyResource.CONFIRMATION_HEADER_TEXT_REJECT,
          subtitle:
            copyResource.CONFIRMATION_HEADER_SUBTEXT ===
            defaultCopyResource.CONFIRMATION_HEADER_SUBTEXT
              ? defaultConfirmationSubtext
              : copyResource.CONFIRMATION_HEADER_SUBTEXT,
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.ERROR,
          alerts: undefined,
        };
      default:
        return {
          title: t("Something went wrong"),
          subtitle: t("Please exit out and try again."),
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.ERROR,
          alerts: undefined,
        };
    }
  }, [
    navigateToCheckout,
    returnSubmissionResultStatus,
    copyResource,
    needToPayDraftOrder,
    returnData?.draftOrderURL,
    alerts,
    defaultConfirmationSubtext,
    defaultCopyResource.CONFIRMATION_HEADER_SUBTEXT,
    defaultCopyResource.CONFIRMATION_HEADER_TEXT_REJECT,
    defaultCopyResource.CONFIRMATION_HEADER_TEXT_SUBMIT,
    defaultCopyResource.CONFIRMATION_HEADER_TEXT_SUBMIT_WITH_ERRORS,
    defaultCopyResource.DRAFT_ORDER_HEADER,
    defaultCopyResource.DRAFT_ORDER_SUBTITLE,
    defaultDraftOrderHeaderText,
    defaultDraftOrderSubtitleText,
    defaultRejectHeaderText,
    defaultSubmitHeaderText,
    defaultSubmitWithErrorsHeaderText,
    hasPendingNanitRmaLabel,
    isPackageProtectionPlus,
    pendingNanitRmaLabelSubtext,
    t,
  ]);

  const trackable =
    returnData && orders && orders[0]
      ? returnToTrackable(
          returnData as any, //TODO, eventually type this function
          orders[0] as any,
          new Set(settings?.redoCoverageProductIds ?? []),
        )
      : undefined;

  const stepData = useSteps({
    settings,
    trackable,
    hasUnresolvedPickupWarnings: hasUnresolvedPickupWarnings ?? false,
    upsellBillingTrigger: returnData?.upsellBillingTrigger ?? undefined,
    isPackageProtectionPlus,
    hasPendingNanitRmaLabel,
  });

  const deadlineDays = Math.max(
    0,
    Math.round(
      Temporal.Now.instant()
        // FIXME Our types are all wonky - expirationDate is really a string, not a Date object
        .until(
          returnData?.expirationDate?.toTemporalInstant() ??
            Temporal.Now.instant(),
        )
        .total("days"),
    ),
  );
  const showDeadline =
    returnData?.products.some((product) => !product.green_return) &&
    !!returnData?.expirationDate &&
    deadlineDays <= 10;

  const isExchange =
    returnData?.advancedExchangeItems?.length ||
    returnData?.products.some(
      (product) => product.exchange_for || product.exchangeGroupItem,
    );

  const showAddresses = !!returnData?.shipping_address;
  const showNewOrderAddress = showAddresses && !!isExchange;

  function addressToInfo(
    address: ReturnAddressZod,
    title: string,
    type: ReturnAddressType,
  ): AddressInfo {
    return {
      address: ReturnAddressToAddress(address),
      title,
      addressText1: address.name || "",
      addressText2: address.address1,
      addressText3: address.address2 ?? undefined,
      addressText4: filterTruthy([
        address.city,
        address.province,
        address.zip,
      ]).join(", "),
      type,
    };
  }
  const returnAddress: AddressInfo | undefined = returnData?.shipping_address
    ? addressToInfo(
        returnData.shipping_address,
        t("Return items from:"),
        ReturnAddressType.SHIPPING,
      )
    : undefined;

  let newOrderAddressInfo: AddressInfo | undefined;
  if (showNewOrderAddress) {
    if (returnData.newOrderAddress) {
      newOrderAddressInfo = addressToInfo(
        returnData.newOrderAddress,
        t("Shipping new items to:"),
        ReturnAddressType.NEW_ORDER,
      );
    } else {
      newOrderAddressInfo = {
        title: t("Shipping new items to:"),
        addressText1: "",
        type: ReturnAddressType.NEW_ORDER,
      };
    }
  }

  const returnQueryError = returnQuery.error;
  const errorCode =
    returnQueryError !== null &&
    typeof returnQueryError === "object" &&
    "code" in returnQueryError
      ? (returnQueryError.code as GetReturnErrorCode)
      : undefined;

  let errorCodeData: ErrorCodeInfo | undefined;
  switch (errorCode) {
    case GetReturnErrorCode.ReturnNotFound:
      errorCodeData = {
        type: GetReturnErrorCode.ReturnNotFound,
        data: { texts: { title: t("We we're unable to locate your return.") } },
      };
      break;
    case undefined:
      errorCodeData = undefined;
      break;
    default:
      assertNever(errorCode);
  }

  const returnItemsText =
    returnType === ReturnTypeEnum.RETURN
      ? t("Return Details")
      : returnType === ReturnTypeEnum.CLAIM
        ? t("Claim Details")
        : returnType === ReturnTypeEnum.WARRANTY
          ? t("Warranty Details")
          : t("Recycle Details");

  const displayCurrency = shouldConvertCurrency
    ? presentmentCurrency
    : baseCurrency;
  const returnTotals = returnTotalsSetQuery.data
    ? getFieldsFromReturnTotalsSet({
        returnTotalsSet: returnTotalsSetQuery.data,
      })
    : undefined;

  return {
    ...stepData,
    returnHeader,
    error:
      returnQuery.error instanceof Error
        ? returnQuery.error.message
        : undefined,
    errorCodeData,
    loadingReturn: useTemporalCreationStatus
      ? returnQuery.isPending || returnQuery.data === undefined
      : returnQuery.data?.returnData === undefined,

    rejected,

    rejectHtml: localizeCustomerPortalFlowText(
      returnData?.products[0]?.rejectMessage ?? "",
    ),
    customSuccessMessage: settings?.theme.custom_confirmation_text,
    warnings,
    deadlineDays,
    showDeadline,
    newItems,
    products: returnData?.products.map(
      function toParams(product): ReturnItemParams {
        // Find the corresponding ProductTotals to get the correct originalPrice
        const productTotals = returnTotals?.productTotals?.find(
          (pt) =>
            pt.product.line_item_id?.toString() ===
            product.line_item_id?.toString(),
        );

        // Use originalPrice from ProductTotals if available, otherwise fallback to product.price
        const originalPrice =
          productTotals?.originalPrice?.toString() ?? product.price;

        return {
          id: product._id,
          pendingReturnItemId: product._id,
          imageSrc: product.images[0] ?? "",
          title: product.product_title,
          subtitle: subtitleWithoutDefault(product.variant_title),
          quantity: product.quantity,
          variants: [],
          formattedPrice: originalPrice,
          isSelected: true,
          currency: displayCurrency,
          price: originalPrice,
          itemValue: productTotals?.discountPrice?.toString() ?? "0",
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate,
          collections: [],
        };
      },
    ),
    currency: displayCurrency,
    newItemsHeader:
      copyResource.NEW_ITEMS_SECTION_HEADER ===
      defaultCopyResource.NEW_ITEMS_SECTION_HEADER
        ? returnType === ReturnTypeEnum.CLAIM
          ? t("Replacement items:")
          : t("New items:")
        : copyResource.NEW_ITEMS_SECTION_HEADER,
    showAddresses,
    newOrderAddress: newOrderAddressInfo,
    customerInfo: {
      firstName: returnData?.shipping_address?.name?.split(" ")[0] ?? "",
      lastName: returnData?.shipping_address?.name?.split(" ")[1] ?? "",
      email: returnData?.shipping_address?.email ?? "",
      id: returnData?.customer?.shopify_customer_id ?? "",
    },
    returnAddress,
    summaryFeesHeader:
      copyResource.FEES_HEADER === defaultCopyResource.FEES_HEADER
        ? returnType === ReturnTypeEnum.CLAIM
          ? t("Claim value")
          : returnType === ReturnTypeEnum.WARRANTY
            ? t("Warranty claim value")
            : returnType === ReturnTypeEnum.RECYCLE
              ? t("Recycling value")
              : t("Return value")
        : copyResource.FEES_HEADER,
    returnTotals,
    summaryFeesOrderHeader:
      copyResource.FEES_ORDER_HEADER === defaultCopyResource.FEES_ORDER_HEADER
        ? returnType === ReturnTypeEnum.CLAIM
          ? t("Replacement order")
          : t("New order")
        : copyResource.FEES_ORDER_HEADER,
    returnItemsHeader:
      copyResource.RETURN_ITEMS_HEADER ===
      defaultCopyResource.RETURN_ITEMS_HEADER
        ? returnType === ReturnTypeEnum.CLAIM
          ? t("Claim items")
          : returnType === ReturnTypeEnum.WARRANTY
            ? t("Warranty claim items")
            : returnType === ReturnTypeEnum.RECYCLE
              ? t("Recycling items")
              : t("Return items")
        : copyResource.RETURN_ITEMS_HEADER,
    draftOrderUrl: returnData?.draftOrderURL,
    texts: {
      rejectedText: t("This item was rejected"),
      returnItems: returnItemsText,
      newItems: t("New items"),
    },
    labelDeducted: returnData?.labelDeductedFromCredit ?? false,
    showCreationStatus,
    creationStatus: creationStatusResult.creationStatus,
    returnType,
    shouldShowSummary: !allProductsAreRepair(returnData?.products),
    manualLabelInfo: {
      showManualLabel: returnData?.isManualLabel === true,
      instructions: settings?.returns?.manualLabelInstructions,
    },
    manualLabelData: returnData?.manualLabelData,
    showReturnSteps: !returnData?.isManualLabel,
    upsellBillingTrigger:
      returnData?.upsellBillingTrigger as UpsellBillingTrigger,
    presentmentCurrency: String(displayCurrency),
    exchangeRate,
    returnData,
    pickupModals,
  };
};
